import { Button, Input } from 'antd';
import { Formik } from 'formik';
import React from 'react';

import FormSelectField from '../../../foundation/components/form_select_field/FormSelectField';
import FullPageLoader from '../../../foundation/components/full_page_loader/FullPageLoader';
import Modal from '../../../foundation/components/modal/Modal';
import { Variant } from '../redux/types';
import { useCreateVariantModalLogic } from './hooks';
import { createVariantFormValidationSchema } from './validation_schema';

type CreateVariantModalTypeProps = {
  closeModal: () => void;
  setNewVariantMetaData: React.Dispatch<React.SetStateAction<Variant | null>>;
  setVariantMetaDataModalVisibility: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  searchedString: string | undefined;
  onVariantCreationStarted: () => void;
  setNewVariantInfo: React.Dispatch<
    React.SetStateAction<{
      transcript_primaryid: string;
      cdna: string;
    } | null>
  >;
};

export const CreateVariantModal = ({
  closeModal,
  setNewVariantMetaData,
  setNewVariantInfo,
  setVariantMetaDataModalVisibility,
  searchedString,
  onVariantCreationStarted,
}: CreateVariantModalTypeProps) => {
  // All logic extracted to custom hook for separation of concerns
  const {
    isLoading,
    transcripts,
    modalOpts,
    initialValues,
    handleDirectCreate,
    fetchGenes,
    handleGeneChange,
  } = useCreateVariantModalLogic({
    closeModal,
    setNewVariantMetaData,
    setNewVariantInfo,
    setVariantMetaDataModalVisibility,
    searchedString,
    onVariantCreationStarted,
  });

  return (
    <Modal {...modalOpts} className="h-create-variant-modal">
      {isLoading && <FullPageLoader />}
      <Formik
        initialValues={initialValues}
        onSubmit={handleDirectCreate}
        validationSchema={createVariantFormValidationSchema}
        enableReinitialize
      >
        {({
          errors,
          touched,
          handleChange,
          values,
          handleSubmit,
          handleBlur,
          isValid,
          dirty,
          setFieldValue,
        }) => {
          return (
            <form onSubmit={handleSubmit}>
              <FormSelectField
                label="Gene"
                onChange={handleGeneChange(setFieldValue)}
                value={values.gene_id}
                error={errors.gene_id}
                isDebounce
                fetchOptions={fetchGenes}
              />
              {values.gene_id && (
                <FormSelectField
                  label="Transcript"
                  onChange={(v: string) => {
                    setFieldValue('transcript_primaryid', v);
                  }}
                  value={values.transcript_primaryid}
                  error={errors.transcript_primaryid}
                  isTable
                  options={transcripts.map((transcript) => {
                    const option = {
                      ...transcript,
                      label: transcript.transcript_external_id,
                      value: transcript.transcript_external_id,
                      primary_id: transcript.transcript_external_id,
                      enst_id: transcript.enst_id || 'N/A',
                      mane_transcript_type:
                        transcript.mane_transcript_type || 'N/A',
                    };
                    return option;
                  })}
                  columns={[
                    { key: 'transcript_external_id', title: 'Transcript' },
                    { key: 'enst_id', title: 'Match transcript' },
                    { key: 'mane_transcript_type', title: 'Selection' },
                  ]}
                />
              )}

              {values.transcript_primaryid && (
                <div className="c-form-field">
                  <div className="c-form-field__label">cDNA:</div>
                  <div className="c-form-field__wrapper">
                    <Input
                      name="cdna"
                      onChange={(e) => {
                        handleChange(e);
                      }}
                      type="text"
                      value={values.cdna}
                      onBlur={handleBlur}
                      placeholder="c.6612C>A"
                    />
                    <div className="c-form-field__error">
                      {errors.cdna ? errors.cdna : undefined}
                    </div>
                  </div>
                </div>
              )}

              {isValid && dirty && (
                <div className="c-form__btn-wrapper h-proband-modal__form-btn-wrapper">
                  <Button
                    type="primary"
                    onClick={() => handleDirectCreate(values)}
                    className="h-proband-modal__form-submit-btn"
                    disabled={!(isValid && dirty) || isLoading}
                  >
                    Continue
                  </Button>
                </div>
              )}
            </form>
          );
        }}
      </Formik>
    </Modal>
  );
};
